import React from 'react';
import { Grid, Typography } from '@mui/material';
import { Translate } from 'react-localize-redux';
import Modal from '../components/general/modal';
import { ButtonCancel, ButtonContinue } from '../components/general/mini_components';

/**
 * SHER-395 — shared "no forfait session, bill the consultation?" confirm flow.
 *
 * A physiotherapy check-in against a patient with no available forfait session
 * makes the API return errorCode "PATIENT_NOT_VOUCHER". Instead of surfacing a
 * dead-end error, the check-in opens a confirm dialog; on confirm it retries the
 * check-in with forceBilling so the consultation is billed (PENDING_PAYMENT).
 *
 * Both outpatients check-in entry points (the consultation queue and the patient
 * appointment list) reuse this hook so the dialog and retry are defined once.
 */
export function isPatientNotVoucherError(err: any): boolean {
    return err?.response?.data?.errorCode === 'PATIENT_NOT_VOUCHER';
}

export function useVoucherBillingConfirm() {
    const [open, setOpen] = React.useState(false);
    const onConfirmRef = React.useRef<(() => void) | null>(null);

    /**
     * If `err` is the "no forfait session" error, stash the billing retry and open
     * the dialog, returning true so the caller can stop its normal error handling.
     * Pass only on the first attempt (not on the forced retry) to avoid a loop.
     */
    function handleCheckInError(err: any, retryWithBilling: () => void): boolean {
        if (isPatientNotVoucherError(err)) {
            onConfirmRef.current = retryWithBilling;
            setOpen(true);
            return true;
        }
        return false;
    }

    function cancel() {
        setOpen(false);
        onConfirmRef.current = null;
    }

    function confirm() {
        const retry = onConfirmRef.current;
        setOpen(false);
        onConfirmRef.current = null;
        retry?.();
    }

    const voucherConfirmModal = (
        <Modal open={open} closeModal={cancel}
            title={<Translate id="pages.hospital.outpatients.appointment.confirm_bill_voucher.title" />}>
            <Grid container spacing={2} style={{ padding: '1rem' }}>
                <Grid item xs={12}>
                    <Typography variant="body1">
                        <Translate id="pages.hospital.outpatients.appointment.confirm_bill_voucher.message" />
                    </Typography>
                </Grid>
                <Grid item xs={12} style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                    <ButtonCancel onClick={cancel}>
                        <Translate id="general.cancel" />
                    </ButtonCancel>
                    &nbsp;
                    <ButtonContinue onClick={confirm}>
                        <Translate id="pages.hospital.outpatients.appointment.confirm_bill_voucher.confirm" />
                    </ButtonContinue>
                </Grid>
            </Grid>
        </Modal>
    );

    return { handleCheckInError, voucherConfirmModal };
}
